import React from 'react';
import PropTypes from 'prop-types';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import { usePrefs, useRequestData } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { trackEvent } from 'ddc-track-event';
import { getTargetLocations } from '../utilities/getTargetLocations';
import ConditionalWrapper from './ConditionalWrapper';

const VEHICLE_BADGE = 'vehicle-badge';
const VEHICLE_BADGE_CUSTOM = `${VEHICLE_BADGE}-custom`;
const VEHICLE_MEDIA = 'vehicle-media';
const VEHICLE_MEDIA_CUSTOM = `${VEHICLE_MEDIA}-custom`;

const Callout = ({ callouts, vehicleTitle, isTabbedLayout }) => {
	const { widgetName, windowId } = useRequestData();

	// This section allows us to use a preference to determine which vehicles should get the regular
	// WIAPI insert locations and which ones should have 'custom' locations added instead.
	const { restrictInsertLocationsForVehicles } = usePrefs();

	const targetLocations = getTargetLocations(vehicleTitle, restrictInsertLocationsForVehicles);

	const isRestricted = (restrictInsertLocationsForVehicles && restrictInsertLocationsForVehicles.length > 0);

	let vehicleBadgeLocation = isRestricted ? `${VEHICLE_BADGE} ${VEHICLE_BADGE_CUSTOM}` : VEHICLE_BADGE;
	let vehicleMediaLocation = isRestricted ? `${VEHICLE_MEDIA} ${VEHICLE_MEDIA_CUSTOM}` : VEHICLE_MEDIA;

	if (targetLocations.size > 0) {
		if (targetLocations.has(VEHICLE_BADGE)) {
			vehicleBadgeLocation = VEHICLE_BADGE_CUSTOM;
		}
		if (targetLocations.has(VEHICLE_MEDIA)) {
			vehicleMediaLocation = VEHICLE_MEDIA_CUSTOM
		}
	}

	const badgeName = 'vehicle-card-badge';

	const calloutItems = [];

	if (targetLocations.size === 0 || !targetLocations.has('vehicle-ctas')) {
		calloutItems.push(
			...callouts.map(
				({
					imageSrc,
					imageAlt,
					imageTitle,
					badgeClasses,
					href = null,
					target = null,
					tooltip = ''
				}) => {
					const img = (
						<img src={imageSrc} alt={imageAlt} title={imageTitle} />
					);

					const hasTooltip = !!tooltip && typeof tooltip === 'string';
					/* eslint-disable react/no-danger */
					const tooltipMarkup = (
						<Popover id="popover">
							<span dangerouslySetInnerHTML={{ __html: tooltip }} />
						</Popover>
					);
					/* eslint-enable react/no-danger */

					const decodedHref =
						href !== null ? href.replace(/&amp;/g, '&') : '';

					const markup = href ? (
						<a
							href={decodedHref}
							className={setClassNames([...badgeClasses])}
							target={target}
							onClick={() =>
								trackEvent(widgetName, windowId, {
									element: 'Badge button',
									elementCTA: imageTitle
								})
							}
							rel={href.startsWith('http') ? 'nofollow' : undefined}
						>
							{img}
						</a>
					) : (
						img
					);

					return (
						<li
							key={imageTitle}
							className={setClassNames([
								badgeName,
								...badgeClasses,
								'm-2',
								'py-3'
							])}
						>
							<ConditionalWrapper
								condition={hasTooltip}
								wrapper={children => (
									<OverlayTrigger
										placement="top"
										overlay={tooltipMarkup}
									>
										{children}
									</OverlayTrigger>
								)}
							>
								{markup}
							</ConditionalWrapper>
						</li>
					);
				}
			)
		);
	}

	const badgeWrapperClasses = [
		'list-unstyled',
		'd-flex',
		'flex-wrap',
		'justify-content-center'
	];

	if (calloutItems.length === 0) {
		badgeWrapperClasses.push('hidden');
	}

	return (
		<div className="badge-callout">
			<ul
				className={setClassNames(badgeWrapperClasses)}
				data-location={
					isTabbedLayout
						? vehicleBadgeLocation
						: `${vehicleMediaLocation} ${vehicleBadgeLocation}`
				}
			>
				{calloutItems}
			</ul>
		</div>
	);
};

Callout.propTypes = {
	callouts: PropTypes.arrayOf(PropTypes.shape({})),
	vehicleTitle: PropTypes.string,
	isTabbedLayout: PropTypes.bool
};

Callout.defaultProps = {
	callouts: [],
	vehicleTitle: '',
	isTabbedLayout: false
};

export default Callout;
